import { TableText } from "@/components/table-tools";
import { useState } from "react";
import { DESKTOP_DOWNLOAD_URL } from "../../../desktop/src/ai-handoff-content";
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";

import {
  listDevices,
  renameDevice,
  revokeDevice,
} from "@/gen/nodebase/v1/device-DeviceService_connectquery";
import { DeviceState, type Device } from "@/gen/nodebase/v1/device_pb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/confirm";
import { EmptyNote, ErrorNote, Loading, PageHeader } from "@/components/page";
import { AccountScope } from "@/components/scope";
import {
  ago,
  enumLabel,
  stateTone,
  transportLabel,
  tunnelLive,
} from "@/lib/format";
import { useSession } from "@/lib/session";

export function Devices() {
  const session = useSession();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<Device | undefined>();
  // Which row is being renamed, and the text typed so far. Held here rather
  // than in the row so leaving edit mode cannot leave a stale draft behind.
  const [editing, setEditing] = useState<string | undefined>();
  const [draft, setDraft] = useState("");
  const [duplicates, setDuplicates] = useState(0);

  const refreshDevices = () =>
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listDevices,
        cardinality: undefined,
      }),
    });

  const devices = useQuery(listDevices, {});
  const revoke = useMutation(revokeDevice, { onSuccess: refreshDevices });
  const rename = useMutation(renameDevice, {
    onSuccess: (res) => {
      setDuplicates(res.duplicateDeviceIds.length);
      setEditing(undefined);
      return refreshDevices();
    },
  });

  const rows = devices.data?.devices ?? [];

  return (
    <>
      <PageHeader title="Devices" scope={<AccountScope noun="Devices" />} />
      <p className="mb-4 text-sm text-muted-foreground">
        Computers and phones linked to this account. Rename them to recognize
        each one, or revoke a device to remove its access.
      </p>
      <ErrorNote error={devices.error ?? revoke.error ?? rename.error} />

      {/* Duplicate names are allowed, so this is a note and not an error. It
          is worth saying at all because two identical rows are indistinguishable
          at a glance, and the address is what tells them apart. */}
      {duplicates > 0 && (
        <div
          data-testid="duplicate-name-note"
          className="mb-4 rounded-md border px-3 py-2 text-sm text-muted-foreground"
        >
          {duplicates} other device{duplicates > 1 ? "s" : ""} already use this
          name. That is allowed — the address column tells them apart.
        </div>
      )}

      {devices.isPending ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyNote>
          No devices on {session?.email}. <a className="underline" href={DESKTOP_DOWNLOAD_URL} target="_blank" rel="noreferrer">Download Desktop</a>,
          sign in with the same email, and choose Connect to join this computer.
        </EmptyNote>
      ) : (
        <Table aria-label="Devices">
          <TableHeader>
            <TableRow>
              <TableHead>Device</TableHead>
              <TableHead>Health</TableHead>
              <TableHead title="Overlay address, tunnel carrier and endpoint port; hover the connection for handshake and report freshness">
                Network
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((d) => {
              const state = enumLabel(DeviceState, d.state);
              // Worker-observed handshake is ground truth for "the tunnel
              // carries packets"; the device's own view fills in when the
              // worker has not reported this peer yet.
              const handshake =
                d.wgHandshakeUnix > 0n ? d.wgHandshakeUnix : d.linkHandshakeUnix;
              return (
                <TableRow key={d.id} data-testid="device-row">
                  <TableCell>
                    {editing === d.id ? (
                      <form
                        className="flex items-center gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          void rename.mutateAsync({
                            deviceId: d.id,
                            name: draft,
                          });
                        }}
                      >
                        <Input
                          autoFocus
                          value={draft}
                          aria-label="Device name"
                          data-testid="rename-input"
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setEditing(undefined);
                          }}
                        />
                        <Button
                          type="submit"
                          size="sm"
                          disabled={rename.isPending || draft.trim() === ""}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing(undefined)}
                        >
                          Cancel
                        </Button>
                      </form>
                    ) : (
                      <div className="font-medium"><TableText value={d.name || "—"} /></div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {d.platform || "—"} · {d.clientVersion || "—"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={stateTone(state)}>{state}</Badge>
                    <div className="mt-1 text-xs text-muted-foreground">
                      seen {ago(d.lastSeenUnix)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <code>{d.overlayIp || "—"}</code>
                    </div>
                    {d.linkReportedUnix > 0n ? (
                      <span
                        title={`last handshake ${ago(handshake)} · reported ${ago(d.linkReportedUnix)}`}
                      >
                        <code>
                          {transportLabel(d.linkTransport)} :
                          {d.linkEndpointPort || "?"}
                        </code>{" "}
                        <Badge
                          variant={tunnelLive(handshake) ? "success" : "secondary"}
                        >
                          {tunnelLive(handshake) ? "live" : "stale"}
                        </Badge>
                      </span>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* A revoked device stays in the list — it is a record of
                        what happened, not a live peer — so there is nothing
                        left to revoke, and renaming a record is noise. */}
                    {d.state !== DeviceState.REVOKED && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="rename-device"
                          onClick={() => {
                            setDuplicates(0);
                            setDraft(d.name);
                            setEditing(d.id);
                          }}
                        >
                          Rename
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setTarget(d)}
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={target !== undefined}
        onOpenChange={() => setTarget(undefined)}
        title={`Revoke ${target?.name || target?.id}?`}
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        description={
          <p>
            It loses workspace access and SSH authorization as soon as the new
            network config reaches the peers. To come back it must run{" "}
            <code>nodebase login</code> and register again.
          </p>
        }
        onConfirm={async () => {
          if (!target) return;
          await revoke.mutateAsync({ deviceId: target.id });
          setTarget(undefined);
        }}
      />
    </>
  );
}
